import type {
  DiagramRotation,
  NextDiagramRotation,
  ReadDiagramRotation,
  WriteDiagramRotation,
} from "./types";

const STORAGE_VERSION = 1;

const TURNS: DiagramRotation[] = [0, 90, 180, 270];

export const diagramRotationStorageKey = (documentKey: string): string =>
  `karl.diagram.rotation.v${STORAGE_VERSION}.${documentKey}`;

const isRotation = (value: unknown): value is DiagramRotation =>
  TURNS.includes(value as DiagramRotation);

/**
 * The orientation belongs to the DOCUMENT, not to one viewing and not to one
 * page.
 *
 * A sheet is sideways because of how the whole file was produced, so the same
 * schematic is sideways on every page of it — and he is checking one circuit
 * after another across that schematic. Per viewing, he would straighten page 4,
 * close it, and straighten it again on the next ask. Per page, he would
 * straighten all forty. Both are the annoyance he asked us to remove rather
 * than a fix for it.
 *
 * The cost of document scope is one press when a document genuinely mixes
 * orientations, because correcting the page in front of him re-orients the
 * rest. That is self-correcting — whatever he is looking at is right after one
 * press — and it needs no second mechanism, which per-page-with-a-default does.
 */
export const readDiagramRotation: ReadDiagramRotation = ({ documentKey }) => {
  if (!documentKey) return 0;
  try {
    const raw = window.localStorage.getItem(
      diagramRotationStorageKey(documentKey),
    );
    if (raw === null) return 0;
    const parsed: unknown = JSON.parse(raw);
    return isRotation(parsed) ? parsed : 0;
  } catch {
    // Private mode, a full quota, or something else's junk under our key. An
    // unreadable preference is not worth a broken modal.
    return 0;
  }
};

export const writeDiagramRotation: WriteDiagramRotation = ({
  documentKey,
  rotation,
}) => {
  if (!documentKey) return;
  try {
    const key = diagramRotationStorageKey(documentKey);
    // Upright is the absence of a preference, so it takes the entry away rather
    // than leaving a row saying nothing.
    if (rotation === 0) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(rotation));
  } catch {
    // Nothing to do about it, and nothing worth interrupting him for.
  }
};

/** One press, one quarter turn clockwise; four presses come back. */
export const nextDiagramRotation: NextDiagramRotation = (current) =>
  TURNS[(TURNS.indexOf(current) + 1) % TURNS.length];
